import { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Loader2, Plus, UserPlus } from 'lucide-react';
import { api } from '../api';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';
import NotaExplicativa from '../ui/NotaExplicativa';

/* =====================================================================
   Prospectos · ERP-OPERATIVO EC1 (el pipeline)
   =====================================================================
   El embudo arriba (cuántos en cada etapa), la lista abajo, y en cada
   prospecto: moverlo de etapa, cotizarle o armarle un pedido. Ganarlo lo
   vuelve cliente sin recapturar; perderlo pide motivo.
   ===================================================================== */
export default function Prospectos() {
  const avisos = useAvisos();
  const [tick, setTick] = useState(0);
  const recargar = useCallback(() => setTick((t) => t + 1), []);
  const [etapas, setEtapas] = useState([]);
  const [embudo, setEmbudo] = useState([]);
  const [lista, setLista] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [error, setError] = useState('');
  const [alta, setAlta] = useState(false);
  const [moviendo, setMoviendo] = useState(null);

  useEffect(() => {
    let vivo = true;
    const q = filtro ? `?etapa=${filtro}` : '';
    Promise.all([
      api.get('/comercial/pipeline/etapas'), api.get('/comercial/pipeline/embudo'),
      api.get(`/comercial/pipeline/prospectos${q}`),
    ]).then(([e, em, l]) => { if (vivo) { setEtapas(e); setEmbudo(em); setLista(l); setError(''); } })
      .catch((err) => { if (vivo) setError(err.message); });
    return () => { vivo = false; };
  }, [tick, filtro]);

  if (error && !lista) return <p className="p-4 text-sm text-red-700">{error}</p>;
  if (!lista) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo el embudo de ventas…
      </p>
    );
  }

  return (
    <div className="flex max-w-5xl flex-col gap-5 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Prospectos</h1>
        <p className="text-gray-600">Personas y empresas que no son clientes, con la etapa en que van.</p>
        <Ayuda id="comercial.pipeline.pantalla" />
      </header>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setFiltro('')} aria-pressed={filtro === ''}
                className={`rounded-xl border px-3 py-2 text-sm ${filtro === '' ? 'border-[var(--color-marca)] bg-[var(--color-marca-suave)]' : 'border-gray-200'}`}>
          Todos
        </button>
        {embudo.map((e) => (
          <button key={e.clave} type="button" onClick={() => setFiltro(e.clave)} aria-pressed={filtro === e.clave}
                  className={`rounded-xl border px-3 py-2 text-sm ${filtro === e.clave ? 'border-[var(--color-marca)] bg-[var(--color-marca-suave)]' : 'border-gray-200'}`}>
            {e.nombre} <span className="font-semibold">{e.prospectos}</span>
          </button>
        ))}
      </div>

      <button type="button" onClick={() => setAlta((v) => !v)}
              className="inline-flex w-fit items-center gap-2 rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold text-white">
        <Plus className="h-4 w-4" aria-hidden="true" /> Nuevo prospecto
      </button>
      {alta && <FormularioProspecto onHecho={(m) => { avisos.exito(m); setAlta(false); recargar(); }} />}

      {lista.length === 0 ? (
        <NotaExplicativa titulo="No hay prospectos aquí">
          Da de alta al primero con un teléfono o un correo. Cuando lo ganes, TEGO lo pasa a Clientes con sus datos.
        </NotaExplicativa>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
          {lista.map((p) => (
            <li key={p.id} className="px-3 py-2 text-sm">
              <div className="flex flex-wrap items-center gap-x-3">
                <UserPlus className="h-4 w-4 text-gray-400" aria-hidden="true" />
                <span className="font-medium text-gray-900">{p.nombre}</span>
                {p.empresa && <span className="text-gray-500">{p.empresa}</span>}
                <span className="text-gray-500">{p.telefono || p.email}</span>
                <span className={`rounded px-2 text-xs ${p.cierra === 'GANADO' ? 'bg-green-50 text-green-800' : p.cierra === 'PERDIDO' ? 'bg-red-50 text-red-800' : 'bg-gray-100 text-gray-700'}`}>
                  {p.etapa_nombre || p.etapa}
                </span>
                {!p.cierra && (
                  <button type="button" onClick={() => setMoviendo(moviendo?.id === p.id ? null : p)}
                          className="ml-auto inline-flex items-center gap-1 font-semibold text-[var(--color-marca)]">
                    <ArrowRight className="h-3 w-3" aria-hidden="true" /> Mover
                  </button>
                )}
              </div>
              {moviendo?.id === p.id && (
                <Mover prospecto={p} etapas={etapas}
                       onHecho={(m) => { avisos.exito(m); setMoviendo(null); recargar(); }} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FormularioProspecto({ onHecho }) {
  const [f, setF] = useState({ nombre: '', empresa: '', telefono: '', email: '', origen: 'OTRO' });
  const [error, setError] = useState('');
  const listo = f.nombre.trim().length >= 2 && (f.telefono.trim() || f.email.trim());
  async function guardar(e) {
    e.preventDefault();
    try {
      const r = await api.post('/comercial/pipeline/prospectos', {
        nombre: f.nombre.trim(), empresa: f.empresa || null, telefono: f.telefono || null,
        email: f.email || null, origen: f.origen,
      });
      onHecho(r.mensaje);
    } catch (err) { setError(err.message); }
  }
  return (
    <form onSubmit={guardar} className="grid gap-3 rounded-xl border border-gray-200 p-4 sm:grid-cols-2">
      <label className="flex flex-col text-sm">Nombre *
        <input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} className="rounded-lg border border-gray-300 p-2" />
      </label>
      <label className="flex flex-col text-sm">Empresa
        <input value={f.empresa} onChange={(e) => setF({ ...f, empresa: e.target.value })} className="rounded-lg border border-gray-300 p-2" />
      </label>
      <label className="flex flex-col text-sm">Teléfono
        <input value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} className="rounded-lg border border-gray-300 p-2" />
      </label>
      <label className="flex flex-col text-sm">Correo
        <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-lg border border-gray-300 p-2" />
      </label>
      <label className="flex flex-col text-sm">De dónde salió
        <select value={f.origen} onChange={(e) => setF({ ...f, origen: e.target.value })} className="rounded-lg border border-gray-300 p-2">
          {['REFERIDO', 'ESCAPARATE', 'LLAMADA', 'VISITA', 'REDES', 'OTRO'].map((o) => <option key={o}>{o}</option>)}
        </select>
      </label>
      {error && <p className="text-sm text-red-700 sm:col-span-2">{error}</p>}
      <div className="flex items-center gap-3 sm:col-span-2">
        <button type="submit" disabled={!listo} className="rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold text-white disabled:bg-gray-300">Guardar</button>
        {!listo && <span className="text-xs text-amber-700">Hace falta el nombre y un teléfono o un correo.</span>}
      </div>
    </form>
  );
}

function Mover({ prospecto, etapas, onHecho }) {
  const [a, setA] = useState(etapas.find((e) => e.clave !== prospecto.etapa)?.clave || '');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const destino = etapas.find((e) => e.clave === a);
  const pideMotivo = destino?.cierra === 'PERDIDO';
  async function mover() {
    try {
      const r = await api.post(`/comercial/pipeline/prospectos/${prospecto.id}/mover`, { a_etapa: a, motivo: motivo || null });
      onHecho(r.mensaje);
    } catch (err) { setError(err.message); }
  }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 p-2">
      <select value={a} onChange={(e) => setA(e.target.value)} className="rounded-lg border border-gray-300 p-2 text-sm">
        {etapas.filter((e) => e.clave !== prospecto.etapa).map((e) => <option key={e.clave} value={e.clave}>{e.nombre}</option>)}
      </select>
      {pideMotivo && (
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo de la pérdida *"
               className="rounded-lg border border-gray-300 p-2 text-sm" />
      )}
      <button type="button" onClick={mover} disabled={pideMotivo && !motivo.trim()}
              className="rounded-xl bg-[var(--color-marca)] px-3 py-1.5 text-sm font-semibold text-white disabled:bg-gray-300">
        Mover a {destino?.nombre || ''}
      </button>
      <Ayuda id="comercial.pipeline.perdido" />
      {error && <span className="text-sm text-red-700">{error}</span>}
    </div>
  );
}
